import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Crown, XCircle, ChevronRight } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const MAX_POLLS = 12;      // ~24s
const POLL_INTERVAL = 2000;

export function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const { refresh } = useAuth();
  const [status, setStatus] = useState('checking'); // checking | paid | error | expired
  const nav = useNavigate();
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setStatus('paid');
          if (refresh) await refresh();
          return;
        }
        if (data.payment_status === 'expired' || data.status === 'failed') {
          setStatus('expired');
          return;
        }
        if (tries.current >= MAX_POLLS) { setStatus('error'); return; }
        setTimeout(poll, POLL_INTERVAL);
      } catch (err) {
        console.error('Payment status polling failed', err);
        setStatus('error');
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen bg-aura-bg grid place-items-center px-5 relative overflow-hidden">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <div className="absolute -top-32 -right-24 h-96 w-96 rounded-full bg-aura-gold/25 blur-3xl" />
      <div className="relative aura-card p-8 md:p-10 max-w-md w-full text-center" data-testid="payment-success-card">
        {status === 'checking' && (
          <>
            <div className="animate-aura-pulse text-5xl">⚡</div>
            <h1 className="mt-4 font-display font-black text-white text-2xl">Confirmando tu pago…</h1>
            <p className="mt-2 text-sm text-aura-textDim">Esto suele tardar unos segundos.</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <div className="mx-auto grid place-items-center h-20 w-20 rounded-full bg-aura-gold text-black">
              <Crown size={40} strokeWidth={2.5} />
            </div>
            <h1 className="mt-4 font-display font-black text-white text-3xl">¡Eres ELITE!</h1>
            <p className="mt-2 text-sm text-aura-textDim">
              Bienvenido al programa AURA ELITE. Tu acceso está activo de por vida.
              A partir de <b className="text-aura-gold">Octubre 2026</b>, competirás cada mes por 5 gadgets.
            </p>
            <div className="mt-6 flex flex-col gap-2">
              <Link to="/prizes" className="aura-btn-primary w-full !py-3 justify-center" data-testid="btn-see-prizes">
                <CheckCircle2 size={16} /> Ver Premios ELITE
              </Link>
              <Link to="/home" className="aura-btn-ghost w-full !py-3 justify-center">
                Ir al feed <ChevronRight size={14} />
              </Link>
            </div>
          </>
        )}
        {(status === 'error' || status === 'expired') && (
          <>
            <div className="mx-auto grid place-items-center h-20 w-20 rounded-full bg-red-500/20 text-red-400">
              <XCircle size={40} strokeWidth={2.2} />
            </div>
            <h1 className="mt-4 font-display font-black text-white text-2xl">
              {status === 'expired' ? 'Sesión expirada' : 'No pudimos confirmar el pago'}
            </h1>
            <p className="mt-2 text-sm text-aura-textDim">
              Si tu cargo se procesó, tu ELITE se activará en breve. Si tienes dudas, contacta soporte.
            </p>
            <button onClick={() => nav('/plans')} className="aura-btn-ghost w-full mt-6 !py-3 justify-center">
              Volver a paquetes
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancelPage() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-aura-bg grid place-items-center px-5 relative">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <div className="relative aura-card p-8 max-w-md w-full text-center" data-testid="payment-cancel-card">
        <div className="mx-auto grid place-items-center h-16 w-16 rounded-full bg-white/10">
          <XCircle size={32} className="text-white/60" />
        </div>
        <h1 className="mt-4 font-display font-black text-white text-2xl">Pago cancelado</h1>
        <p className="mt-2 text-sm text-aura-textDim">No se realizó ningún cargo. Puedes volver a intentarlo cuando quieras.</p>
        <button onClick={() => nav('/plans')} className="aura-btn-primary w-full mt-6 !py-3 justify-center">
          Volver a paquetes
        </button>
      </div>
    </div>
  );
}
